import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useRouterState, useNavigate } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, FileTypes, PhotoDimensions } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PhotoCamera2 } from '../../../../components/PhotoCamera2';
import { Button } from '../../../../components/buttons/Button';
// import { NextSection } from '../../../../components/buttons/NextSection';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
// import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
// import { getNextPage } from '../../../../data/navigation/getNextPage';
// import { NavForward } from '../../../../components/buttons/NavForward';
import { Uploader } from '../../../../components/Uploader';

import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
import { ChartInitError } from '../../../../components/ChartInitError';

import * as Sentry from '@sentry/browser';


import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/info/photoForCard')({
  component: PhotoForCard,
});

function PhotoForCard() {
  const { mutate, mutateAsync } = useUpdateChartSection();
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  // const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [warned, setWarned] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  const photoForCardInit = useInitChartSection({
    needed: !!chart && !chart.PHOTO_FOR_CARD,
    write: () => mutateAsync({ patientId: patientId, docData: { PHOTO_FOR_CARD: { portraitURI: '' } } }),
  });

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType?.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // MARK PREVIOUS PAGE AS COMPLETED
  // React.useEffect(() => {
  //   if (patientInfo && patientInfo.navObj) {
  //     markPreviousPageCompleted({
  //       currentPageName: 'photoForCard',
  //       patientInfo,
  //       value: true,
  //     });
  //   }
  // }, [patientInfo]);

  // // GET NEXT PAGE URL
  // React.useEffect(() => {
  //   if (chart && patientInfo.navObj) {
  //     (async () => {
  //       const awaitedUrl = await getNextPage({
  //         currentPage: 'photoForCard',
  //         chart: chart,
  //         patientInfo: patientInfo,
  //       });
  //       setNextPageURL(awaitedUrl);
  //     })();
  //   }
  // }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_ID: {
            issuingState: idForm.state.values.issuingState,
            licenseNumber: idForm.state.values.idNumber,

            photoIdentificationFrontURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
            photoIdentificationBackURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const idForm = useForm({
    defaultValues: {
      issuingState: chart?.PHOTO_ID?.issuingState ? chart.PHOTO_ID.issuingState : '',
      idNumber: chart?.PHOTO_ID?.licenseNumber ? chart?.PHOTO_ID.licenseNumber : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PHOTO_FOR_CARD) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col">
          <Heading id="photo-for-card" textContent="Photo for Card" />
          <div className="flex flex-col items-center">
            <p className="text-xl">You will now take a self-portrait.</p>
            <p className="text-xl">This photo will be printed on your MIDEO identity card.</p>
            <p className="text-xl">It should resemble a traditional government ID photo.</p>
          </div>
          <div id="photo-for-card-container" className="my-4">
            <div className="flex flex-col items-center">
              {chart?.PHOTO_FOR_CARD?.portraitURI && takingPhoto.portrait === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={chart.PHOTO_FOR_CARD?.portraitURI}
                    style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                    alt="Current Portrait"
                  />
                  <div className="ml-4">
                    <Button
                      id="retake-portrait"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => setTakingPhoto({ ...takingPhoto, portrait: true })}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.portrait === false && (
                  <Button
                    id="take-portrait"
                    type="button"
                    textContent="Take Photo"
                    onClick={() => setTakingPhoto({ ...takingPhoto, portrait: true })}
                  />
                )
              )}
              {takingPhoto.portrait && (
                <PhotoCamera2
                  type={FileTypes.portrait}
                  docToUpdate="PHOTO_FOR_CARD"
                  style={'portrait'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/portrait`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoOneWorking(false);
                    setTakingPhoto({ ...takingPhoto, portrait: false });
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
            {/* ALLOW CONCIERGE PATIENTS TO UPLOAD A PHOTO */}
            {patientInfo.navType?.base === 'concierge' && (
              <div className="my-12 flex flex-col items-center">
                <p className="text-xl">Or upload a photo:</p>
                <Uploader
                  docToUpdate="PHOTO_FOR_CARD"
                  bucket="patient"
                  type={FileTypes.portrait}
                  title="Upload Photo"
                  elementId="photo-file-upload"
                />
              </div>
            )}
            <div id="nav" className="mt-4 flex flex-col items-center">
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={!chart.PHOTO_FOR_CARD?.portraitURI}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
              {/* {patientInfo.navType.base === 'self' && nextPageURL && (
                <NavForward to={nextPageURL} disabled={!chart.PHOTO_FOR_CARD?.portraitURI} />
              )}
              {patientInfo?.conciergeNavigator?.documentsUnlocked && (
                <div className="mt-4 place-self-center">
                  <NextSection to={nextPageURL ? nextPageURL : '../'} />
                </div>
              )} */}
            </div>
          </div>
        </div>
      </>
    );
  }

  // THE PAGE RENDERS ONLY ONCE ITS SECTION EXISTS, SO A REJECTED INIT WOULD OTHERWISE LEAVE IT ON
  // "Loading..." FOREVER. OFFER THE RETRY INSTEAD (CU-17tm466jq2e).
  if (photoForCardInit.failed) {
    return <ChartInitError onRetry={photoForCardInit.rearm} />;
  }

  return <div>Loading...</div>;
}
